import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { CheckCircle, Error } from '@material-ui/icons';

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: theme.spacing(4),
    maxWidth: '1000px',
    margin: '0 auto',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  table: {
    marginTop: theme.spacing(3),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
  },
  successBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.success.light,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  successText: {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.success.dark,
  },
  errorBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  errorText: {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.error.dark,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
  },
  loadingText: {
    marginTop: theme.spacing(2),
    fontSize: '16px',
  },
}));

interface Props {
  data: any;
  onBack: () => void;
  onComplete: () => void;
  isDeactivating: boolean;
}

export function DeactivatedStep({ data, onBack, onComplete, isDeactivating }: Props) {
  const classes = useStyles();

  if (isDeactivating) {
    return (
      <Box className={classes.container}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <Typography className={classes.loadingText}>Deactivating volunteers...</Typography>
        </Box>
      </Box>
    );
  }

  const deactivated = data?.deactivated || [];
  const errors = data?.errors || [];

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>Deactivation Complete</Typography>
      <Typography className={classes.subtitle}>
        The deactivation process has completed. Review the results below.
      </Typography>

      {deactivated.length > 0 && (
        <Box className={classes.successBox}>
          <CheckCircle />
          <Typography className={classes.successText}>
            Successfully deactivated {deactivated.length} volunteer{deactivated.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {errors.length > 0 && (
        <Box className={classes.errorBox}>
          <Error />
          <Typography className={classes.errorText}>
            {errors.length} volunteer{errors.length !== 1 ? 's' : ''} could not be deactivated
          </Typography>
        </Box>
      )}

      {deactivated.length > 0 && (
        <>
          <Typography variant='h6' style={{ marginTop: '2rem', marginBottom: '1rem' }}>
            Successfully Deactivated
          </Typography>
          <TableContainer component={Paper} className={classes.table}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Volunteer ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deactivated.map((volunteer: any, index: number) => (
                  <TableRow key={volunteer.volunteerId || volunteer.email}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{volunteer.email}</TableCell>
                    <TableCell>{volunteer.name}</TableCell>
                    <TableCell>{volunteer.volunteerId}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {errors.length > 0 && (
        <>
          <Typography variant='h6' style={{ marginTop: '2rem', marginBottom: '1rem' }}>
            Errors
          </Typography>
          <TableContainer component={Paper} className={classes.table}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((error: any, index: number) => (
                  <TableRow key={error.email}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{error.email}</TableCell>
                    <TableCell>{error.error}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Box className={classes.buttonContainer}>
        <Button variant='outlined' onClick={onBack}>
          Back
        </Button>
        <Button variant='contained' color='primary' onClick={onComplete}>
          Done
        </Button>
      </Box>
    </Box>
  );
}
